/**
 * Column-label prettifier.
 *
 * The replacement map mirrors `_format_column_name` in `app/api/query.py` so a
 * header reads the same whether it came from the backend's own prose answer or
 * from this table. Keep the two in sync when the backend map changes.
 */
const EXPLICIT_LABELS: Record<string, string> = {
  BrokerID: 'Broker ID',
  DealerID: 'Dealer ID',
  NetPL: 'Net P&L',
  DayPL: 'Day P&L',
  OpngPL: 'Opening P&L',
  MtM: 'MTM',
  LTP: 'LTP',
  ExpMargin: 'Exposure Margin',
  TotMargin: 'Total Margin',
  ShortMargin: 'Short Margin',
  ExpiryDate: 'Expiry Date',
  StrikePrice: 'Strike Price',
  OptType: 'Option Type',
  LastUpdateTime: 'Last Update Time',
}

export function formatColumnName(column: string): string {
  if (EXPLICIT_LABELS[column]) return EXPLICIT_LABELS[column]

  return String(column ?? '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

const CURRENCY_HINTS = ['pl', 'p&l', 'margin', 'price', 'mtm', 'amount', 'value']

/** Columns that read better with fixed 2-decimal money formatting. */
export function isMonetaryColumn(column: string): boolean {
  const name = String(column ?? '').toLowerCase()
  return CURRENCY_HINTS.some((hint) => name.includes(hint))
}

const IDENTIFIER_HINTS = ['id', 'code', 'no', 'number', 'ref']

/**
 * Identifier-ish columns are numeric but must never be grouped — `1,234` is a
 * wrong rendering of broker 1234.
 */
export function isIdentifierColumn(column: string): boolean {
  const name = String(column ?? '').toLowerCase()
  return IDENTIFIER_HINTS.some(
    (hint) => name === hint || name.endsWith(hint) || name.endsWith(`_${hint}`),
  )
}
